import React, { useState, useEffect, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TextInput,
  TouchableOpacity,
  ScrollView,
  SafeAreaView,
  ActivityIndicator,
  Platform,
  Image,
} from 'react-native';
import { useFocusEffect } from '@react-navigation/native';
import Icon from 'react-native-vector-icons/MaterialIcons';
import * as ImagePicker from 'expo-image-picker';
import { useToast } from '../contexts/ToastContext';
import { useAuth } from '../services/AuthContext';
import { createHotspot, updateHotspot } from '../services/database';
import { uploadFile, getHotspotPhotoUrl, STORAGE_BUCKETS } from '../services/storage';

const HOTSPOT_CATEGORIES = [
  { key: 'PARK', label: 'Park', icon: '🌳' },
  { key: 'CAFE', label: 'Cafe', icon: '☕' },
  { key: 'BAR', label: 'Bar', icon: '🍺' },
  { key: 'CLUB', label: 'Chess Club', icon: '♟️' },
  { key: 'UNIVERSITY', label: 'University', icon: '🎓' },
  { key: 'PUBLIC_SQUARE', label: 'Public Square', icon: '📍' },
  { key: 'OTHER', label: 'Other', icon: '♟️' },
];

const AddEditHotspotScreen = ({ route, navigation }) => {
  const { hotspot } = route.params || {};
  const isEditing = !!hotspot;
  const { showToast } = useToast();
  const { appUser } = useAuth();
  const [vettingState, setVettingState] = useState(
    hotspot ? { vetted: !!hotspot.links_vetted, at: hotspot.links_vetted_at, by: hotspot.links_vetted_by } : null
  );
  
  // Track if we've already processed coordinates from map picker
  const coordinatesProcessedRef = React.useRef(false);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: 'PARK',
    city: '',
    address: '',
    latitude: '',
    longitude: '',
    activityLevel: '',
    schedule: '',
    amenities: '',
    // Tags and social links
    tags: '',
    instagramUrl: '',
    twitterUrl: '',
    facebookUrl: '',
    websiteUrl: '',
  });
  const [photos, setPhotos] = useState([]);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    const loadHotspotData = async () => {
      if (hotspot) {
        // Support both camelCase and snake_case field names (Supabase returns snake_case)
        setFormData({
          name: hotspot.name || '',
          description: hotspot.description || '',
          category: hotspot.category || 'PARK',
          city: hotspot.city || '',
          address: hotspot.address || '',
          latitude: String(hotspot.latitude || ''),
          longitude: String(hotspot.longitude || ''),
          activityLevel: hotspot.activityLevel || hotspot.activity_level || '',
          schedule: hotspot.schedule || '',
          amenities: hotspot.amenities ? hotspot.amenities.join(', ') : '',
          // Tags and social links
          tags: hotspot.tags ? hotspot.tags.join(', ') : '',
          instagramUrl: hotspot.instagram_url || '',
          twitterUrl: hotspot.twitter_url || '',
          facebookUrl: hotspot.facebook_url || '',
          websiteUrl: hotspot.website_url || '',
        });
        
        // Convert photo keys/URLs to displayable URLs
        if (hotspot.photos && hotspot.photos.length > 0) {
          const photoUrls = hotspot.photos.map((photo) => {
            // If it's already a URL, use it
            if (photo && photo.startsWith('http')) {
              return photo;
            }
            // Otherwise, get the public URL from Supabase storage
            return getHotspotPhotoUrl(photo) || photo;
          });
          setPhotos(photoUrls.filter(url => url)); // Filter out any null/undefined
        } else {
          setPhotos([]);
        }
        setVettingState({ vetted: !!hotspot.links_vetted, at: hotspot.links_vetted_at || null, by: hotspot.links_vetted_by || null });
      }
    };
    
    loadHotspotData();
  }, [hotspot]);

  // Reset coordinates processed flag when screen comes into focus
  useFocusEffect(
    React.useCallback(() => {
      coordinatesProcessedRef.current = false;
    }, [])
  );

  // Handle coordinates returned from MapPickerScreen
  useEffect(() => {
    if (route.params?.selectedCoordinates && !coordinatesProcessedRef.current) {
      try {
        const { latitude, longitude } = route.params.selectedCoordinates;
        setFormData(prev => ({
          ...prev,
          latitude: String(latitude),
          longitude: String(longitude),
        }));
        coordinatesProcessedRef.current = true;
        showToast('Location updated from map', 'success');
        
        // Clear the param to prevent re-applying on re-render
        navigation.setParams({ selectedCoordinates: null });
      } catch (error) {
        console.error('Error processing coordinates:', error);
      }
    }
  }, [route.params?.selectedCoordinates, navigation, showToast]);

  const handleInputChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  const handlePickImages = async () => {
    console.log('🎯 handlePickImages called - opening image picker...');
    showToast('Opening photo picker...', 'info');
    
    try {
      // Request permission first on native
      if (Platform.OS !== 'web') {
        const { status } = await ImagePicker.requestMediaLibraryPermissionsAsync();
        if (status !== 'granted') {
          showToast('Permission to access photos was denied', 'error');
          return;
        }
      }

      console.log('📂 Launching image library (Platform:', Platform.OS, ')...');
      const result = await ImagePicker.launchImageLibraryAsync({
        mediaTypes: ImagePicker.MediaTypeOptions.Images,
        allowsMultipleSelection: true,
        quality: 0.8,
      });

      console.log('🔍 ImagePicker result:', JSON.stringify(result, null, 2));

      // Handle both cancelled (British) and canceled (American) spellings
      // Also handle result.assets (native), result.selected (web with allowsMultipleSelection), and result.uri (legacy)
      const isCanceled = result.cancelled !== undefined ? result.cancelled : result.canceled;
      
      if (isCanceled) {
        console.log('❌ Image picker was canceled by user');
        return;
      }

      let imagesToUpload = [];
      
      // Try result.assets first (native platforms with multiple selection)
      if (result.assets && result.assets.length > 0) {
        imagesToUpload = result.assets.filter(asset => asset.uri);
        console.log('✅ Found', imagesToUpload.length, 'images in result.assets');
      } 
      // Try result.selected (web platform with allowsMultipleSelection: true)
      else if (result.selected && result.selected.length > 0) {
        imagesToUpload = result.selected.filter(asset => asset.uri);
        console.log('✅ Found', imagesToUpload.length, 'images in result.selected');
      }
      // Fallback to result.uri (legacy single selection)
      else if (result.uri) {
        imagesToUpload = [{ uri: result.uri }];
        console.log('✅ Found image in result.uri (legacy format)');
      }

      if (imagesToUpload.length === 0) {
        console.warn('⚠️ No images found in picker result');
        showToast('No images selected', 'warning');
        return;
      }

      setUploading(true);
      showToast(`Uploading ${imagesToUpload.length} photo(s)...`, 'info');
      console.log('📸 Starting upload of', imagesToUpload.length, 'photos...');
      
      const newPhotos = await Promise.all(
        imagesToUpload.map(async (asset) => {
          try {
            console.log('📤 Fetching image from URI:', asset.uri?.substring(0, 50) + '...');
            
            // Convert image to blob
            const response = await fetch(asset.uri);
            const blob = await response.blob();
            console.log('📦 Blob created, size:', blob.size, 'bytes');
            
            // Generate unique filename
            const filename = `${Date.now()}-${Math.random().toString(36).substring(7)}.jpg`;
            console.log('📤 Uploading file:', filename);
            
            // Upload to Supabase storage
            const { data, error } = await uploadFile(
              STORAGE_BUCKETS.HOTSPOT_PHOTOS,
              filename,
              blob,
              { contentType: 'image/jpeg' }
            );

            if (error) {
              console.error('❌ Upload error:', error);
              throw error;
            }

            // Get public URL for display
            const url = getHotspotPhotoUrl(filename);
            console.log('✅ Photo uploaded, URL:', url);
            return url;
          } catch (uploadError) {
            console.error('❌ Error uploading individual photo:', uploadError);
            throw uploadError;
          }
        })
      );

      const validPhotos = newPhotos.filter(url => url);
      setPhotos([...photos, ...validPhotos]);
      console.log('📸 All photos uploaded. Total:', [...photos, ...validPhotos].length);
      showToast(`${validPhotos.length} photo(s) uploaded successfully!`, 'success');
      
    } catch (error) {
      console.error('❌ Error in handlePickImages:', error);
      showToast('Failed to upload photos: ' + (error.message || String(error)), 'error');
    } finally {
      setUploading(false);
    }
  };

  const handleRemovePhoto = (index) => {
    setPhotos(photos.filter((_, i) => i !== index));
  };

  const validateForm = () => {
    if (!formData.name.trim()) {
      showToast('Please enter a name', 'error');
      return false;
    }
    if (!formData.description.trim()) {
      showToast('Please enter a description', 'error');
      return false;
    }
    if (!formData.address.trim()) {
      showToast('Please enter an address', 'error');
      return false;
    }
    if (!formData.latitude || !formData.longitude) {
      showToast('Please enter latitude and longitude', 'error');
      return false;
    }

    const lat = parseFloat(formData.latitude);
    const lng = parseFloat(formData.longitude);
    if (isNaN(lat) || isNaN(lng) || lat < -90 || lat > 90 || lng < -180 || lng > 180) {
      showToast('Invalid coordinates', 'error');
      return false;
    }

    return true;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    try {
      setSaving(true);

      const input = {
        name: formData.name.trim(),
        description: formData.description.trim(),
        category: formData.category,
        city: formData.city.trim() || null,
        address: formData.address.trim(),
        latitude: parseFloat(formData.latitude),
        longitude: parseFloat(formData.longitude),
        photos: photos.length > 0 ? photos : null,
        activity_level: formData.activityLevel.trim() || null,
        schedule: formData.schedule.trim() || null,
        amenities: formData.amenities
          ? formData.amenities.split(',').map((a) => a.trim()).filter((a) => a)
          : null,
        // Tags and social links
        tags: formData.tags
          ? formData.tags.split(',').map((t) => t.trim().toLowerCase().replace(/\s+/g, '-')).filter((t) => t)
          : [],
        instagram_url: formData.instagramUrl.trim() || null,
        twitter_url: formData.twitterUrl.trim() || null,
        facebook_url: formData.facebookUrl.trim() || null,
        website_url: formData.websiteUrl.trim() || null,
      };

      if (isEditing) {
        // Update existing hotspot
        await updateHotspot(hotspot.id, input);
        showToast('Hotspot updated successfully', 'success');
      } else {
        // Create new hotspot
        await createHotspot(input);
        showToast('Hotspot created successfully', 'success');
      }

      navigation.goBack();
    } catch (error) {
      console.error('Error saving hotspot:', error);
      showToast(`Failed to ${isEditing ? 'update' : 'create'} hotspot: ${error.message}`, 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <SafeAreaView style={styles.container}>
      {/* Header */}
      <View style={styles.header}>
        <TouchableOpacity style={styles.backButton} onPress={() => navigation.goBack()}>
          <Icon name="arrow-back" size={24} color="#333" />
        </TouchableOpacity>
        <Text style={styles.headerTitle}>
          {isEditing ? 'Edit Hotspot' : 'New Hotspot'}
        </Text>
        <View style={{ width: 28 }} />
      </View>

      <ScrollView style={styles.content} showsVerticalScrollIndicator={false}>
        {/* Name */}
        <View style={styles.formGroup}>
          <Text style={styles.label}>Name *</Text>
          <TextInput
            style={styles.input}
            value={formData.name}
            onChangeText={(value) => handleInputChange('name', value)}
            placeholder="e.g., Washington Square Park"
            placeholderTextColor="#999"
          />
        </View>

        {/* Category */}
        <View style={styles.formGroup}>
          <Text style={styles.label}>Category *</Text>
          <View style={styles.categoryGrid}>
            {HOTSPOT_CATEGORIES.map((cat) => (
              <TouchableOpacity
                key={cat.key}
                style={[
                  styles.categoryButton,
                  formData.category === cat.key && styles.categoryButtonActive,
                ]}
                onPress={() => handleInputChange('category', cat.key)}
              >
                <Text style={styles.categoryIcon}>{cat.icon}</Text>
                <Text
                  style={[
                    styles.categoryLabel,
                    formData.category === cat.key && styles.categoryLabelActive,
                  ]}
                >
                  {cat.label}
                </Text>
              </TouchableOpacity>
            ))}
          </View>
        </View>

        {/* Description */}
        <View style={styles.formGroup}>
          <Text style={styles.label}>Description *</Text>
          <TextInput
            style={[styles.input, styles.textArea]}
            value={formData.description}
            onChangeText={(value) => handleInputChange('description', value)}
            placeholder="Describe what makes this a great chess spot..."
            placeholderTextColor="#999"
            multiline
            numberOfLines={4}
          />
        </View>

        {/* City */}
        <View style={styles.formGroup}>
          <Text style={styles.label}>City</Text>
          <TextInput
            style={styles.input}
            value={formData.city}
            onChangeText={(value) => handleInputChange('city', value)}
            placeholder="e.g., New York, Los Angeles, Chicago"
            placeholderTextColor="#999"
          />
          <Text style={styles.hint}>Used for filtering and organizing hotspots</Text>
        </View>

        {/* Address */}
        <View style={styles.formGroup}>
          <Text style={styles.label}>Address *</Text>
          <TextInput
            style={styles.input}
            value={formData.address}
            onChangeText={(value) => handleInputChange('address', value)}
            placeholder="e.g., Washington Square Park, New York, NY"
            placeholderTextColor="#999"
          />
        </View>

        {/* Coordinates */}
        <View style={styles.formGroup}>
          <Text style={styles.label}>Coordinates *</Text>
          <View style={styles.coordinatesRow}>
            <View style={styles.coordinateInput}>
              <Text style={styles.coordinateLabel}>Latitude</Text>
              <TextInput
                style={styles.input}
                value={formData.latitude}
                onChangeText={(value) => handleInputChange('latitude', value)}
                placeholder="40.7308"
                placeholderTextColor="#999"
                keyboardType="numeric"
              />
            </View>
            <View style={styles.coordinateInput}>
              <Text style={styles.coordinateLabel}>Longitude</Text>
              <TextInput
                style={styles.input}
                value={formData.longitude}
                onChangeText={(value) => handleInputChange('longitude', value)}
                placeholder="-73.9975"
                placeholderTextColor="#999"
                keyboardType="numeric"
              />
            </View>
          </View>
          <TouchableOpacity
            style={styles.mapPickerButton}
            onPress={() => {
              navigation.navigate('MapPicker', {
                initialLatitude: formData.latitude ? parseFloat(formData.latitude) : null,
                initialLongitude: formData.longitude ? parseFloat(formData.longitude) : null,
                hotspot: hotspot,
                hotspotId: hotspot?.id,
              });
            }}
          >
            <Icon name="map" size={20} color="#94EB98" />
            <Text style={styles.mapPickerButtonText}>Pick Location on Map</Text>
          </TouchableOpacity>
          <Text style={styles.hint}>Tip: Use the map picker or enter coordinates manually</Text>
        </View>

        {/* Schedule */}
        <View style={styles.formGroup}>
          <Text style={styles.label}>Schedule</Text>
          <TextInput
            style={styles.input}
            value={formData.schedule}
            onChangeText={(value) => handleInputChange('schedule', value)}
            placeholder="e.g., Mon-Fri 2pm-8pm"
            placeholderTextColor="#999"
          />
        </View>

        {/* Activity Level */}
        <View style={styles.formGroup}>
          <Text style={styles.label}>Activity Level</Text>
          <TextInput
            style={styles.input}
            value={formData.activityLevel}
            onChangeText={(value) => handleInputChange('activityLevel', value)}
            placeholder="e.g., Busy on weekends"
            placeholderTextColor="#999"
          />
        </View>

        {/* Amenities */}
        <View style={styles.formGroup}>
          <Text style={styles.label}>Amenities</Text>
          <TextInput
            style={styles.input}
            value={formData.amenities}
            onChangeText={(value) => handleInputChange('amenities', value)}
            placeholder="e.g., Outdoor seating, Coffee available, Free WiFi"
            placeholderTextColor="#999"
          />
          <Text style={styles.hint}>Separate multiple amenities with commas</Text>
        </View>

        {/* Tags */}
        <View style={styles.formGroup}>
          <Text style={styles.label}>Tags</Text>
          <TextInput
            style={styles.input}
            value={formData.tags}
            onChangeText={(value) => handleInputChange('tags', value)}
            placeholder="e.g., coffee-shop, cocktail-bar, hi-fi-lounge"
            placeholderTextColor="#999"
          />
          <Text style={styles.hint}>Additional descriptors, separated by commas (stored as kebab-case)</Text>
        </View>

        {/* Social Links Section */}
        <View style={styles.formGroup}>
          <Text style={styles.sectionLabel}>Social Media Links</Text>
          
          <View style={styles.socialInputRow}>
            <Icon name="camera-alt" size={20} color="#E4405F" style={styles.socialIcon} />
            <TextInput
              style={styles.socialInput}
              value={formData.instagramUrl}
              onChangeText={(value) => handleInputChange('instagramUrl', value)}
              placeholder="Instagram URL"
              placeholderTextColor="#999"
              autoCapitalize="none"
              keyboardType="url"
            />
          </View>

          <View style={styles.socialInputRow}>
            <Icon name="chat" size={20} color="#1DA1F2" style={styles.socialIcon} />
            <TextInput
              style={styles.socialInput}
              value={formData.twitterUrl}
              onChangeText={(value) => handleInputChange('twitterUrl', value)}
              placeholder="Twitter/X URL"
              placeholderTextColor="#999"
              autoCapitalize="none"
              keyboardType="url"
            />
          </View>

          <View style={styles.socialInputRow}>
            <Icon name="thumb-up" size={20} color="#1877F2" style={styles.socialIcon} />
            <TextInput
              style={styles.socialInput}
              value={formData.facebookUrl}
              onChangeText={(value) => handleInputChange('facebookUrl', value)}
              placeholder="Facebook URL"
              placeholderTextColor="#999"
              autoCapitalize="none"
              keyboardType="url"
            />
          </View>

          <View style={styles.socialInputRow}>
            <Icon name="language" size={20} color="#4CAF50" style={styles.socialIcon} />
            <TextInput
              style={styles.socialInput}
              value={formData.websiteUrl}
              onChangeText={(value) => handleInputChange('websiteUrl', value)}
              placeholder="Website URL"
              placeholderTextColor="#999"
              autoCapitalize="none"
              keyboardType="url"
            />
          </View>

          {isEditing && appUser?.isAdmin && (
            <View style={styles.vettingSection}>
              <Text style={styles.vettingLabel}>
                {vettingState?.vetted
                  ? `Vetted on ${vettingState.at ? new Date(vettingState.at).toLocaleDateString() : '—'}`
                  : 'Not yet vetted'}
              </Text>
              <View style={styles.vettingButtons}>
                <TouchableOpacity
                  style={[styles.vettingButton, styles.approveButton]}
                  onPress={async () => {
                    if (!hotspot?.id || !appUser?.id) return;
                    try {
                      await updateHotspot(hotspot.id, {
                        links_vetted: true,
                        links_vetted_at: new Date().toISOString(),
                        links_vetted_by: appUser.id,
                      });
                      setVettingState({ vetted: true, at: new Date().toISOString(), by: appUser.id });
                      showToast('Links approved', 'success');
                    } catch (e) {
                      showToast('Failed to approve links', 'error');
                    }
                  }}
                >
                  <Icon name="check-circle" size={18} color="#fff" />
                  <Text style={styles.vettingButtonText}>Approve Links</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={[styles.vettingButton, styles.rejectButton]}
                  onPress={async () => {
                    if (!hotspot?.id) return;
                    try {
                      await updateHotspot(hotspot.id, {
                        instagram_url: null,
                        facebook_url: null,
                        twitter_url: null,
                        links_vetted: false,
                        links_vetted_at: null,
                        links_vetted_by: null,
                      });
                      setFormData((prev) => ({ ...prev, instagramUrl: '', twitterUrl: '', facebookUrl: '' }));
                      setVettingState({ vetted: false, at: null, by: null });
                      showToast('Links rejected and cleared', 'success');
                    } catch (e) {
                      showToast('Failed to reject links', 'error');
                    }
                  }}
                >
                  <Icon name="cancel" size={18} color="#fff" />
                  <Text style={styles.vettingButtonText}>Reject Links</Text>
                </TouchableOpacity>
              </View>
            </View>
          )}
        </View>

        {/* Photos */}
        <View style={styles.formGroup}>
          <Text style={styles.label}>Photos</Text>
          <TouchableOpacity 
            style={[styles.uploadButton, uploading && styles.uploadButtonDisabled]} 
            onPress={handlePickImages}
            disabled={uploading}
          >
            {uploading ? (
              <>
                <ActivityIndicator size="small" color="#94EB98" />
                <Text style={styles.uploadButtonText}>Uploading...</Text>
              </>
            ) : (
              <>
                <Icon name="add-photo-alternate" size={24} color="#94EB98" />
                <Text style={styles.uploadButtonText}>
                  {photos.length > 0 ? 'Add More Photos' : 'Upload Photos'}
                </Text>
              </>
            )}
          </TouchableOpacity>
          {photos.length > 0 && (
            <ScrollView horizontal style={styles.photosContainer} showsHorizontalScrollIndicator={false}>
              {photos.map((photo, index) => (
                <View key={index} style={styles.photoWrapper}>
                  <Image
                    source={{ uri: photo }}
                    style={styles.photoImage}
                  />
                  <TouchableOpacity
                    style={styles.removePhotoButton}
                    onPress={() => handleRemovePhoto(index)}
                  >
                    <Icon name="close" size={16} color="white" />
                  </TouchableOpacity>
                </View>
              ))}
            </ScrollView>
          )}
        </View>

        {/* Save Button */}
        <TouchableOpacity
          style={[styles.saveButton, saving && styles.saveButtonDisabled]}
          onPress={handleSave}
          disabled={saving}
        >
          {saving ? (
            <ActivityIndicator size="small" color="white" />
          ) : (
            <>
              <Icon name="check" size={24} color="white" />
              <Text style={styles.saveButtonText}>
                {isEditing ? 'Save Changes' : 'Create Hotspot'}
              </Text>
            </>
          )}
        </TouchableOpacity>

        <View style={{ height: 40 }} />
      </ScrollView>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f5f5f5',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 16,
    backgroundColor: 'white',
    borderBottomWidth: 1,
    borderBottomColor: '#e0e0e0',
  },
  backButton: {
    padding: 4,
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#333',
  },
  content: {
    flex: 1,
    padding: 16,
  },
  formGroup: {
    marginBottom: 24,
  },
  label: {
    fontSize: 15,
    fontWeight: '600',
    color: '#333',
    marginBottom: 8,
  },
  input: {
    backgroundColor: 'white',
    borderWidth: 1,
    borderColor: '#e0e0e0',
    borderRadius: 10,
    paddingHorizontal: 16,
    paddingVertical: 12,
    fontSize: 15,
    color: '#333',
  },
  textArea: {
    height: 100,
    textAlignVertical: 'top',
  },
  hint: {
    fontSize: 12,
    color: '#999',
    marginTop: 6,
  },
  sectionLabel: {
    fontSize: 16,
    fontWeight: '700',
    color: '#333',
    marginBottom: 12,
  },
  socialInputRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
    backgroundColor: 'white',
    borderWidth: 1,
    borderColor: '#e0e0e0',
    borderRadius: 10,
    paddingHorizontal: 12,
  },
  socialIcon: {
    marginRight: 10,
  },
  socialInput: {
    flex: 1,
    paddingVertical: 12,
    fontSize: 15,
    color: '#333',
  },
  vettingSection: {
    marginTop: 16,
    paddingTop: 16,
    borderTopWidth: 1,
    borderTopColor: '#e0e0e0',
  },
  vettingLabel: {
    fontSize: 13,
    color: '#666',
    marginBottom: 10,
  },
  vettingButtons: {
    flexDirection: 'row',
    gap: 12,
  },
  vettingButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingVertical: 10,
    paddingHorizontal: 16,
    borderRadius: 10,
  },
  approveButton: {
    backgroundColor: '#2E7D32',
  },
  rejectButton: {
    backgroundColor: '#c62828',
  },
  vettingButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#fff',
  },
  categoryGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  categoryButton: {
    width: '31%',
    aspectRatio: 1.5,
    backgroundColor: 'white',
    borderWidth: 2,
    borderColor: '#e0e0e0',
    borderRadius: 10,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 8,
  },
  categoryButtonActive: {
    backgroundColor: '#FFF3E0',
    borderColor: '#FF9800',
  },
  categoryIcon: {
    fontSize: 24,
    marginBottom: 4,
  },
  categoryLabel: {
    fontSize: 12,
    fontWeight: '500',
    color: '#666',
    textAlign: 'center',
  },
  categoryLabelActive: {
    color: '#FF9800',
    fontWeight: '600',
  },
  coordinatesRow: {
    flexDirection: 'row',
    gap: 12,
  },
  coordinateInput: {
    flex: 1,
  },
  coordinateLabel: {
    fontSize: 13,
    color: '#666',
    marginBottom: 6,
  },
  photosContainer: {
    marginTop: 12,
    marginBottom: 12,
    paddingVertical: 8,
  },
  photoWrapper: {
    position: 'relative',
    marginRight: 12,
  },
  photoImage: {
    width: 100,
    height: 100,
    borderRadius: 8,
    backgroundColor: '#f0f0f0',
  },
  removePhotoButton: {
    position: 'absolute',
    top: 4,
    right: 4,
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    borderRadius: 12,
    width: 24,
    height: 24,
    justifyContent: 'center',
    alignItems: 'center',
  },
  uploadButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    backgroundColor: '#E8F5E9',
    paddingVertical: 14,
    borderRadius: 10,
    borderWidth: 2,
    borderColor: '#94EB98',
    borderStyle: 'dashed',
  },
  uploadButtonText: {
    fontSize: 15,
    fontWeight: '600',
    color: '#94EB98',
  },
  uploadButtonDisabled: {
    opacity: 0.6,
  },
  saveButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    backgroundColor: '#94EB98',
    paddingVertical: 16,
    borderRadius: 10,
    marginTop: 8,
  },
  saveButtonDisabled: {
    opacity: 0.6,
  },
  saveButtonText: {
    fontSize: 17,
    fontWeight: 'bold',
    color: 'white',
  },
  mapPickerButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    backgroundColor: '#E8F5E9',
    paddingVertical: 12,
    borderRadius: 10,
    borderWidth: 2,
    borderColor: '#94EB98',
    marginTop: 8,
  },
  mapPickerButtonText: {
    fontSize: 15,
    fontWeight: '600',
    color: '#94EB98',
  },
});

export default AddEditHotspotScreen;

